import React, { useState, useRef, useEffect } from 'react';

interface PlotTypeSelectorProps {
  type: 'scatter' | 'trajectory';
  currentValue: string;
  onChange: (value: string) => void;
  color: string;
  className?: string;
}

/**
 * Plot type selector component - shows dropdown when clicking plot type icon
 */
const PlotTypeSelector: React.FC<PlotTypeSelectorProps> = ({
  type,
  currentValue,
  onChange,
  color,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' }>({
    vertical: 'bottom',
    horizontal: 'left',
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Calculate dropdown position based on viewport boundaries
  const calculateDropdownPosition = () => {
    if (!buttonRef.current) return;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 200; // Approximate height of dropdown (4 options * ~50px each)
    const dropdownWidth = 160; // min-w-[140px] + padding
    
    // Check vertical position
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const vertical = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight ? 'top' : 'bottom';
    
    // Check horizontal position
    // spaceRight: space available if dropdown opens from left edge (left-0)
    // spaceLeft: space available if dropdown opens from right edge (right-0)
    const spaceRight = viewportWidth - buttonRect.left;
    const spaceLeft = buttonRect.right;
    const horizontal = spaceRight < dropdownWidth && spaceLeft > dropdownWidth ? 'right' : 'left';
    
    setDropdownPosition({ vertical, horizontal });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      calculateDropdownPosition();
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const scatterOptions = [
    { value: 'circle', label: 'Circle', icon: '●' },
    { value: 'square', label: 'Square', icon: '■' },
    { value: 'triangle', label: 'Triangle', icon: '▲' },
    { value: 'star', label: 'Star', icon: '★' },
  ];

  const trajectoryOptions = [
    { value: 'solid-arrow', label: 'Solid', icon: '→' },
    { value: 'dotted-arrow', label: 'Dotted', icon: '⇢' },
    { value: 'progressive', label: 'Progressive', icon: '⇀' },
  ];

  const options = type === 'scatter' ? scatterOptions : trajectoryOptions;

  const renderIcon = (optionValue: string, optionColor: string) => {
    if (type === 'scatter') {
      switch (optionValue) {
        case 'circle':
          return <div className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: optionColor }}></div>;
        case 'square':
          return <div className="w-3 h-3 border border-gray-300" style={{ backgroundColor: optionColor }}></div>;
        case 'triangle':
          return (
            <svg className="w-3 h-3" viewBox="0 0 12 12" fill={optionColor}>
              <path d="M6 2 L10 10 L2 10 Z" stroke="#ffffff" strokeWidth="0.5" />
            </svg>
          );
        case 'star':
          return (
            <svg className="w-3 h-3" viewBox="0 0 12 12" fill={optionColor}>
              <path d="M6 1 L7 4 L10 4 L8 6 L9 9 L6 7 L3 9 L4 6 L2 4 L5 4 Z" stroke="#ffffff" strokeWidth="0.3" />
            </svg>
          );
        default:
          return <div className="w-3 h-3 rounded-full" style={{ backgroundColor: optionColor }}></div>;
      }
    } else {
      // Trajectory icons
      const isDotted = optionValue.includes('dotted');
      const hasArrow = optionValue.includes('arrow');
      const isProgressive = optionValue === 'progressive';
      
      if (isProgressive) {
        // Progressive icon - gradient line from white to color with circle at end
        return (
          <svg className="w-4 h-3" viewBox="0 0 20 10">
            <defs>
              <linearGradient id={`prog-icon-${optionValue}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                <stop offset="100%" stopColor={optionColor} stopOpacity="1" />
              </linearGradient>
            </defs>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
              d="M2 5 Q10 2 18 5"
              stroke={`url(#prog-icon-${optionValue})`}
              fill="none"
            />
            {/* Circle at end */}
            <circle
              cx="18"
              cy="5"
              r="2"
              fill={optionColor}
              stroke="#ffffff"
              strokeWidth="0.5"
            />
          </svg>
        );
      }
      
      // Solid and Dotted icons - consistent styling
      return (
        <svg className="w-4 h-3" fill="none" stroke="currentColor" viewBox="0 0 20 10" style={{ color: optionColor }}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.5"
            d="M2 5 Q10 2 18 5"
            strokeDasharray={isDotted ? '2,2' : 'none'}
          />
          {/* Arrow at end for both solid and dotted */}
          {hasArrow && (
            <path
              d="M16 4 L18 5 L16 6"
              stroke={optionColor}
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      );
    }
  };

  const getDropdownClasses = () => {
    const verticalClass = dropdownPosition.vertical === 'top' ? 'bottom-full mb-1' : 'top-full mt-1';
    const horizontalClass = dropdownPosition.horizontal === 'right' ? 'right-0' : 'left-0';
    return `absolute ${horizontalClass} ${verticalClass} z-50 bg-white border border-gray-300 rounded-lg shadow-lg min-w-[140px] max-h-[200px] overflow-y-auto`;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
        title={`Change ${type} style`}
      >
        {renderIcon(currentValue, color)}
      </button>

      {isOpen && (
        <div className={getDropdownClasses()}>
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-50 flex items-center space-x-2 ${
                  currentValue === option.value ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex-shrink-0">
                  {renderIcon(option.value, color)}
                </div>
                <span className={currentValue === option.value ? 'font-medium text-blue-900' : 'text-gray-700'}>
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlotTypeSelector;

